import { aiColors } from '@euler/app/flows/ai-suite/theme';
import {
  ApdmAudienceRule,
  ApdmCampaignAudienceSource,
  ApdmCampaignRecipientView,
  ApdmCampaignSchedule,
  ApdmOutreachState,
  ApdmReachability,
} from '@euler/model/apdm';
import dayjs from 'dayjs';

/** Who a campaign targets, in the owner's words. */
export function apdmAudienceText(rule: ApdmAudienceRule): string {
  switch (rule.kind) {
    case 'win_back':
      return `超过 ${rule.idleMonths} 个月没到店的客户`;
    case 'maintenance_due':
      return rule.overdueUpToDays
        ? `${rule.withinDays} 天内保养到期、或已过期 ${rule.overdueUpToDays} 天内的客户`
        : `${rule.withinDays} 天内保养到期的客户`;
    case 'all_reachable':
      return '能联系上的全部客户';
    case 'plates':
      return `指定的 ${rule.plates.length} 位客户`;
    case 'query':
      return rule.description;
    default:
      return '按条件挑选的客户';
  }
}

export function apdmAudienceSourcesText(
  sources: ApdmCampaignAudienceSource[],
): string {
  const names = new Set(
    sources.map(source =>
      source.kind === 'platform' ? '门店客户' : '导入的客户表格',
    ),
  );
  return [...names].join('、');
}

export function apdmScheduleText(schedule: ApdmCampaignSchedule): string {
  const format = (value: string) => dayjs(value).format('M月D日');
  const dates =
    schedule.startAt && schedule.endAt
      ? `${format(schedule.startAt)} – ${format(schedule.endAt)}`
      : schedule.startAt
      ? `${format(schedule.startAt)} 起`
      : '';
  const window = `每天 ${schedule.dailyWindow.from}–${schedule.dailyWindow.to} 发送`;
  const cap = schedule.dailyCap ? `，每天最多 ${schedule.dailyCap} 位` : '';
  return [dates, window + cap].filter(Boolean).join(' · ');
}

/** The customer list's filters, in the design's order. */
export type ApdmCustomerFilter = 'all' | ApdmOutreachState | 'undelivered';

export const kApdmCustomerFilters: Array<{
  value: ApdmCustomerFilter;
  label: string;
}> = [
  { value: 'all', label: '全部' },
  { value: ApdmOutreachState.Pending, label: '待发送' },
  { value: ApdmOutreachState.Reached, label: '已触达' },
  { value: ApdmOutreachState.Replied, label: '已回复' },
  { value: ApdmOutreachState.Engaged, label: '有意向' },
  { value: ApdmOutreachState.NoResponse, label: '暂无回复' },
  { value: ApdmOutreachState.DoNotContact, label: '不再联系' },
  { value: ApdmOutreachState.Converted, label: '已转化' },
  { value: 'undelivered', label: '没发出去' },
];

const kStateLook: Record<ApdmOutreachState, { label: string; color: string }> =
  {
    [ApdmOutreachState.Pending]: {
      label: '待发送',
      color: aiColors.mutedForeground,
    },
    [ApdmOutreachState.Reached]: { label: '已触达', color: aiColors.primary },
    [ApdmOutreachState.Replied]: { label: '已回复', color: aiColors.green },
    [ApdmOutreachState.Engaged]: { label: '有意向', color: aiColors.violet },
    [ApdmOutreachState.NoResponse]: {
      label: '暂无回复',
      color: aiColors.mutedForeground,
    },
    [ApdmOutreachState.NotInterested]: {
      label: '暂不感兴趣',
      color: aiColors.mutedForeground,
    },
    [ApdmOutreachState.DoNotContact]: {
      label: '不再联系',
      color: aiColors.destructive,
    },
    [ApdmOutreachState.Converted]: { label: '已转化', color: aiColors.green },
  };

/** How a customer row reads: the undelivered state wins over the rest. */
export function apdmCustomerLook(row: ApdmCampaignRecipientView): {
  label: string;
  color: string;
} {
  if (row.reachability === ApdmReachability.Unreachable)
    return { label: '没发出去', color: aiColors.destructive };
  return kStateLook[row.outreachState];
}

/** A time as the lists show it: today's clock, 昨天, or the date. */
export function apdmShortTime(value: string): string {
  const time = dayjs(value);
  const days = dayjs().startOf('day').diff(time.startOf('day'), 'day');
  return days === 0
    ? time.format('HH:mm')
    : days === 1
    ? '昨天'
    : apdmDay(time.toDate());
}

/** A calendar day; this year's without the year. */
export function apdmDay(value: string | Date): string {
  const day = dayjs(value);
  return day.format(day.year() === dayjs().year() ? 'M月D日' : 'YYYY年M月D日');
}
